import React, {
  useState,
  useRef
} from 'react';
import {
  Box,
  Button,
  Popover,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider
} from '@mui/material';
import { CsvBuilder } from 'filefy'
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Svg,
  Path,
  G,
  Defs,
  ClipPath,
  Rect
} from '@react-pdf/renderer';
import {
  endOfWeek,
  format,
  startOfWeek,
  subWeeks
} from 'date-fns';
import { GetAppOutlined as FileDownload } from '@mui/icons-material'
import { CLOCKS } from 'src/apollo/queries';
import { useQuery } from '@apollo/client';
import { Clock } from 'src/types/__generated__/typescript-operations';
import {
  COMPANY_FULL_NAME,
  COMPANY_ADDRESS
} from 'src/constants/config';
import DateRangeComponent from './DateRangeComponent';

interface TimeSheetExportModalProps {
  id: string,
  type: 'CAREGIVER' | 'PATIENT'
}

// Purple Logo SVG Component for react-pdf
const PurpleLogo = () => (
  <Svg width="104" height="42" viewBox="0 0 104 42">
    <Defs>
      <ClipPath id="clip0_10444_1564">
        <Rect width="104" height="42" fill="white" />
      </ClipPath>
    </Defs>
    <G clipPath="url(#clip0_10444_1564)">
      <Path d="M1.92849 11.365L3.48263 17.1059C3.9452 18.8145 5.69963 19.8232 7.40122 19.3586C9.10282 18.8941 10.1072 17.1324 9.64482 15.4239L8.09058 9.68287C7.62802 7.97416 5.87373 6.96568 4.17204 7.43006C2.47049 7.89454 1.46593 9.6563 1.92849 11.365Z" fill="#8901E1" />
      <Path d="M2.3785 31.4179L8.10687 32.9368C9.81183 33.3889 11.559 32.3676 12.0091 30.6556C12.4593 28.9437 11.4421 27.1894 9.73736 26.7372L4.0088 25.2183C2.30384 24.7661 0.556892 25.7875 0.106527 27.4994C-0.343651 29.2114 0.673539 30.9657 2.3785 31.4179Z" fill="#8901E1" />
      <Path d="M19.8981 41.0527L24.0723 36.8308C25.3147 35.5742 25.3074 33.5442 24.056 32.2968C22.8046 31.0493 20.7829 31.0567 19.5406 32.3131L15.3663 36.5352C14.1239 37.7918 14.1313 39.8215 15.3826 41.0692C16.634 42.3166 18.6557 42.3093 19.8981 41.0527Z" fill="#8901E1" />
      <Path d="M36.9682 30.6348L35.414 24.8939C34.9515 23.1853 33.1971 22.1766 31.4955 22.6412C29.7939 23.1057 28.7894 24.8674 29.2519 26.5759L30.8061 32.3169C31.2687 34.0256 33.023 35.0341 34.7246 34.5697C36.4262 34.1053 37.4308 32.3435 36.9682 30.6348Z" fill="#8901E1" />
      <Path d="M36.5179 10.5822L30.7895 9.06323C29.0846 8.61114 27.3375 9.63244 26.8873 11.3444C26.4371 13.0564 27.4543 14.8107 29.1591 15.2628L34.8876 16.7818C36.5926 17.2339 38.3395 16.2126 38.7899 14.5006C39.2401 12.7887 38.2229 11.0343 36.5179 10.5822Z" fill="#8901E1" />
      <Path d="M18.9983 0.947218L14.824 5.16915C13.5816 6.42573 13.5889 8.45569 14.8404 9.70317C16.0918 10.9506 18.1134 10.9433 19.3557 9.68683L23.53 5.46476C24.7725 4.20818 24.7651 2.1784 23.5137 0.930787C22.2623 -0.316642 20.2407 -0.309366 18.9983 0.947218Z" fill="#8901E1" />
      <Path d="M51.369 32.2602V10.4985H55.8725V32.1993L51.369 32.2602Z" fill="#8901E1" />
      <Path d="M62.1622 9.73975C64.0359 9.73975 64.8523 11.1665 64.8523 12.2589C64.8523 13.2909 64.1268 14.8689 62.1622 14.8689C60.2277 14.8689 59.442 13.3514 59.442 12.2892C59.442 11.2875 60.2277 9.73975 62.1622 9.73975ZM59.8949 32.1689V16.9938H64.3989V32.1689H59.8949Z" fill="#8901E1" />
      <Path d="M68.3911 32.169V16.9938H72.8645V18.9056C74.3155 16.9026 76.6732 16.6903 77.1567 16.6903C83.2929 16.6903 83.6555 21.0304 83.6555 24.0654V32.169H79.1519V23.9439C79.1519 21.88 78.3356 20.666 76.3404 20.666C73.8923 20.666 72.8951 22.4567 72.8951 24.0957V32.169L68.3911 32.169Z" fill="#8901E1" />
      <Path d="M87.4358 10.4985H91.9398V23.0335L98.3778 17.0241H103.698L96.9876 23.4585L104 32.169H98.5897L93.7533 26.3114L91.9398 27.9504V32.169H87.4358V10.4985Z" fill="#8901E1" />
    </G>
  </Svg>
);

// Column configuration with widths
const COLUMN_CONFIG = [
  { key: 'dateWorked', header: 'Date Worked', width: '8%' },
  { key: 'patient', header: 'Patient', width: '9%' },
  { key: 'caregiver', header: 'Caregiver', width: '9%' },
  { key: 'clockIn', header: 'Clock In', width: '10%' },
  { key: 'clockOut', header: 'Clock Out', width: '10%' },
  { key: 'clockInSelfie', header: 'In Selfie', width: '4%' },
  { key: 'clockOutSelfie', header: 'Out Selfie', width: '4%' },
  { key: 'clockInGeo', header: 'Clock In Geo', width: '10%' },
  { key: 'clockOutGeo', header: 'Clock Out Geo', width: '10%' },
  { key: 'hoursPaid', header: 'Hours Paid', width: '5%' },
  { key: 'approvedBy', header: 'Approved By', width: '7%' },
  { key: 'reason', header: 'Reason', width: '7%' },
  { key: 'note', header: 'Note', width: '7%' },
];

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    padding: 25,
  },
  section: {
    flexGrow: 1,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 10,
  },
  logoSection: {
    flexDirection: 'column',
    paddingTop: 5,
  },
  companySection: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  table: {
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableColHeader: {
    padding: 4,
    fontSize: 7,
    fontWeight: 'bold',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    borderBottomStyle: 'solid',
    backgroundColor: '#E8E8E8',
  },
  tableCol: {
    borderBottomWidth: 1,
    borderBottomColor: '#bfbfbf',
    borderBottomStyle: 'solid',
    padding: 4,
    fontSize: 6,
  },
  summaryRow: {
    flexDirection: 'row',
    backgroundColor: '#D0D0D0',
    borderTopWidth: 2,
    borderTopColor: '#000',
    borderTopStyle: 'solid',
  },
  customImage: {
    height: '28px',
    width: '88px',
    marginBottom: '12px',
  },
  customStartDateEndDate: {
    fontSize: '11px',
    fontWeight: 'bold',
    marginBottom: '5px',
    marginTop: '8px',
  },
  textWrapper: {
    fontSize: '16px',
    marginBottom: '6px',
    fontWeight: 'bold',
  },
  textWrapper2: {
    fontSize: '10px',
    marginBottom: '4px',
  },
  textWrapper3: {
    fontSize: '10px',
    marginBottom: '4px',
  },
  emptyState: {
    padding: 20,
    textAlign: 'center',
    fontSize: 12,
    color: '#666',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 8,
    bottom: 10,
    right: 20,
    color: '#666',
  },
});

const TimeSheetExportModal = ({ id, type }: TimeSheetExportModalProps) => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [date, setDate] = useState({
    startDate: startOfWeek(subWeeks(new Date(), 1)),
    endDate: endOfWeek(subWeeks(new Date(), 1))
  });
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    COLUMN_CONFIG.map((col) => col.key)
  );

  const isPatient = type === 'PATIENT';
  const isCaregiver = type === 'CAREGIVER';

  const { data: clocks, loading } = useQuery<{ clocks: Clock[] }>(CLOCKS, {
    variables: {
      data: {
        patientId: isPatient ? id : undefined,
        caregiverId: isCaregiver ? id : undefined,
        dateFrom: date?.startDate || new Date(),
        dateTo: date?.endDate || new Date()
      }
    },
    fetchPolicy: 'no-cache'
  });

  const clocksData = clocks?.clocks || [];
  const firstName = isPatient
    ? clocksData[0]?.patient?.firstName
    : clocksData[0]?.caregiver?.sfContact?.firstName;
  const lastName = isPatient
    ? clocksData[0]?.patient?.lastName
    : clocksData[0]?.caregiver?.sfContact?.lastName;

  const formatDateForFileName = (d: Date | null | undefined): string => {
    if (!d) return 'no-date';
    return format(d, 'MM-dd-yyyy');
  };

  const baseFileName = `TimeSheet-${firstName || 'Unknown'}-${lastName || ''}-${formatDateForFileName(date?.startDate)}-${formatDateForFileName(date?.endDate)}`;

  const getPatientName = (clock: Clock): string => {
    const first = clock.patient?.firstName || '';
    const last = clock.patient?.lastName || '';
    return `${first} ${last}`.trim() || 'Unknown';
  };

  const getCaregiverName = (clock: Clock): string => {
    const first = clock.caregiver?.sfContact?.firstName || '';
    const last = clock.caregiver?.sfContact?.lastName || '';
    return `${first} ${last}`.trim() || 'Unknown';
  };

  const formatGeoLocation = (geo: { latitude?: number; longitude?: number } | null | undefined): string => {
    if (!geo || geo.latitude === undefined || geo.longitude === undefined) return '';
    return `Lat: ${geo.latitude.toFixed(6)}\nLon: ${geo.longitude.toFixed(6)}`;
  };

  const buildRowData = (clock: Clock): Record<string, string | number> => {
    const flags = clock.flags || [];
    const uniqueReasons = Array.from(
      new Set(flags.map((flag) => flag.reason).filter(Boolean))
    ).join(', ');
    const uniqueNotes = Array.from(
      new Set(flags.map((flag) => flag.note).filter(Boolean))
    ).join(', ');
    const uniqueUpdatedBy = Array.from(
      new Set(flags.map((flag) => (flag.statusUpdatedBy
        ? `${flag.statusUpdatedBy.firstName || ''} ${flag.statusUpdatedBy.lastName || ''}`.trim()
        : '')).filter(Boolean))
    ).join(', ');

    return {
      dateWorked: format(new Date(clock.clockIn), 'MM-dd-yyyy'),
      patient: getPatientName(clock),
      caregiver: getCaregiverName(clock),
      clockIn: format(new Date(clock.clockIn), 'MM-dd-yyyy h:mm aa'),
      clockOut: clock.clockOut
        ? format(new Date(clock.clockOut), 'MM-dd-yyyy h:mm aa')
        : 'In Progress',
      clockInSelfie: clock.selfiePath ? 'yes' : 'no',
      clockOutSelfie: clock.selfieClockOutPath ? 'yes' : 'no',
      // TODO: Remove hardcoded sample geo data after testing
      clockInGeo: formatGeoLocation(clock.geolocation),
      clockOutGeo: formatGeoLocation(clock.clockOutGeolocation),
      hoursPaid: clock.estimatedPay || 0,
      approvedBy: uniqueUpdatedBy,
      reason: uniqueReasons,
      note: uniqueNotes,
    };
  };

  const allRowData = clocksData.map(buildRowData);

  const totalHoursPaid = clocksData.reduce(
    (sum, clock) => sum + (clock.estimatedPay || 0),
    0
  );

  const getFilteredColumns = () => {
    const filtered = COLUMN_CONFIG.filter((col) => selectedColumns.includes(col.key));
    // Calculate total original width and normalize to 100%
    const totalOriginalWidth = filtered.reduce((sum, col) => sum + parseFloat(col.width), 0);
    return filtered.map((col) => ({
      ...col,
      width: `${((parseFloat(col.width) / totalOriginalWidth) * 100).toFixed(1)}%`
    }));
  };

  const getFilteredHeaders = () => getFilteredColumns().map((col) => col.header);

  const getFilteredValues = () => allRowData.map((row) => COLUMN_CONFIG.filter((col) => selectedColumns.includes(col.key)).map((col) => row[col.key]?.toString() || ''));

  const handleColumnToggle = (columnKey: string) => {
    setSelectedColumns((prev) => (prev.includes(columnKey)
      ? prev.filter((key) => key !== columnKey)
      : [...prev, columnKey]));
  };

  const handleSelectAll = () => {
    setSelectedColumns(COLUMN_CONFIG.map((col) => col.key));
  };

  const handleDeselectAll = () => {
    setSelectedColumns([]);
  };

  const PDFDocument = () => {
    const filteredColumns = getFilteredColumns();
    const filteredValues = getFilteredValues();
    const hasData = filteredValues.length > 0;
    const startDateStr = date?.startDate ? format(new Date(date.startDate), 'MM-dd-yyyy') : 'Not selected';
    const endDateStr = date?.endDate ? format(new Date(date.endDate), 'MM-dd-yyyy') : 'Not selected';

    const hoursPaidIndex = filteredColumns.findIndex((col) => col.key === 'hoursPaid');
    const isHoursPaidVisible = hoursPaidIndex !== -1;
    const isHoursPaidFirst = hoursPaidIndex === 0;
    const lastColumnIndex = filteredColumns.length - 1;

    const getSummaryCellContent = (col: { key: string }, index: number): string => {
      const isHoursPaidColumn = col.key === 'hoursPaid';
      const isLastColumn = index === lastColumnIndex;

      // Case 1: Hours Paid is visible but is the first column - combine label and value
      if (isHoursPaidColumn && isHoursPaidFirst) {
        return `Total: ${totalHoursPaid.toFixed(2)}`;
      }

      // Case 2: Hours Paid is visible and not first - show value only in Hours Paid column
      if (isHoursPaidColumn) {
        return totalHoursPaid.toFixed(2);
      }

      // Case 3: Hours Paid is visible - show label in the column before it
      if (isHoursPaidVisible && index === hoursPaidIndex - 1) {
        return 'Total Hours Paid:';
      }

      // Case 4: Hours Paid is NOT visible - show combined label and value in last column
      if (!isHoursPaidVisible && isLastColumn) {
        return `Total Hours Paid: ${totalHoursPaid.toFixed(2)}`;
      }

      return '';
    };

    const getSummaryCellAlignment = (col: { key: string }, index: number): 'left' | 'right' => {
      const isHoursPaidColumn = col.key === 'hoursPaid';
      const isLabelColumn = isHoursPaidVisible && !isHoursPaidFirst && index === hoursPaidIndex - 1;
      const isLastColumn = index === lastColumnIndex;
      const showContentHere = isHoursPaidColumn || isLabelColumn || (!isHoursPaidVisible && isLastColumn);

      return showContentHere ? 'right' : 'left';
    };

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page} wrap>
          {/* Header Section - Fixed on first page */}
          <View style={styles.headerContainer} fixed>
            <View style={styles.logoSection}>
              <PurpleLogo />
              <Text style={styles.customStartDateEndDate}>
                Start Date: {startDateStr}
              </Text>
              <Text style={styles.customStartDateEndDate}>
                End Date: {endDateStr}
              </Text>
            </View>
            <View style={styles.companySection}>
              <Text style={styles.textWrapper}>Timesheet</Text>
              <Text style={styles.textWrapper3}>{COMPANY_FULL_NAME}</Text>
              <Text style={styles.textWrapper3}>{COMPANY_ADDRESS}</Text>
              <Text style={styles.textWrapper2}>
                Report Generated: {format(new Date(), 'MM-dd-yyyy')}
              </Text>
            </View>
          </View>

          {/* Table Section */}
          <View style={styles.section}>
            {hasData ? (
              <View style={styles.table} wrap>
                {/* Table Header */}
                <View style={styles.tableRow} fixed>
                  {filteredColumns.map((col, index) => (
                    <Text
                      key={index}
                      style={[styles.tableColHeader, { width: col.width }]}
                    >
                      {col.header}
                    </Text>
                  ))}
                </View>

                {/* Table Rows */}
                {filteredValues.map((row, rowIndex) => (
                  <View
                    key={rowIndex}
                    style={[
                      styles.tableRow,
                      { backgroundColor: rowIndex % 2 === 0 ? '#F3F3F3' : '#DEDEDE' },
                    ]}
                    wrap={false}
                  >
                    {row.map((value, colIndex) => (
                      <Text
                        key={colIndex}
                        style={[
                          styles.tableCol,
                          { width: filteredColumns[colIndex].width },
                        ]}
                      >
                        {value}
                      </Text>
                    ))}
                  </View>
                ))}

                {/* Summary Row */}
                <View style={styles.summaryRow} wrap={false}>
                  {filteredColumns.map((col, index) => (
                    <Text
                      key={index}
                      style={[
                        styles.tableCol,
                        {
                          width: col.width,
                          fontWeight: 'bold',
                          fontSize: 7,
                          textAlign: getSummaryCellAlignment(col, index),
                        },
                      ]}
                    >
                      {getSummaryCellContent(col, index)}
                    </Text>
                  ))}
                </View>
              </View>
            ) : (
              <Text style={styles.emptyState}>
                No records found for the selected date range.
              </Text>
            )}
          </View>

          {/* Page Numbers */}
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
            fixed
          />
        </Page>
      </Document>
    );
  };

  const downloadCSV = () => {
    const csvFileName = `${baseFileName}.csv`;
    new CsvBuilder(csvFileName)
      .setColumns(getFilteredHeaders())
      .addRows(getFilteredValues())
      .exportFile();
  };

  const pdfFileName = `${baseFileName}.pdf`;

  return (
    <>
      <Tooltip title="Timesheet export">
        <Button onClick={() => setOpen(true)} ref={ref}>
          <FileDownload
            fontSize="medium"
            style={{
              cursor: 'pointer',
            }}
          />
        </Button>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        open={isOpen}
      >
        <Box sx={{ p: 2, minWidth: 350, maxWidth: 500 }}>
          {/* Date Range Section */}
          <Typography variant="subtitle2" gutterBottom>
            Date Range
          </Typography>
          <DateRangeComponent
            timePeriodOptions={[]}
            selectionRange={date || { startDate: null, endDate: null }}
            allowClearable
            placeholder="Select date / range"
            style={{ margin: 0 }}
            setSelectionRange={(value) => setDate(value)}
            onTimePeriodChange={(e, value) => setDate(value)}
          />

          <Divider sx={{ my: 2 }} />

          {/* Column Selection Section */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">
              Select Columns
            </Typography>
            <Box>
              <Button size="small" onClick={handleSelectAll}>
                All
              </Button>
              <Button size="small" onClick={handleDeselectAll}>
                None
              </Button>
            </Box>
          </Box>
          <FormGroup sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {COLUMN_CONFIG.map((col) => (
              <FormControlLabel
                key={col.key}
                control={(
                  <Checkbox
                    size="small"
                    checked={selectedColumns.includes(col.key)}
                    onChange={() => handleColumnToggle(col.key)}
                  />
                )}
                label={<Typography variant="body2">{col.header}</Typography>}
              />
            ))}
          </FormGroup>

          <Divider sx={{ my: 2 }} />

          {/* Export Buttons */}
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Loading data...
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                color="primary"
                variant="contained"
                onClick={downloadCSV}
                disabled={selectedColumns.length === 0}
              >
                Export CSV
              </Button>
              <Button
                color="primary"
                variant="contained"
                disabled={selectedColumns.length === 0}
              >
                <PDFDownloadLink
                  document={<PDFDocument />}
                  fileName={pdfFileName}
                  style={{ color: 'white', textDecoration: 'none' }}
                >
                  {/* @ts-ignore */}
                  {({ loading: pdfLoading }) => {
                    if (pdfLoading) return 'Preparing PDF...';
                    return 'Export PDF';
                  }}
                </PDFDownloadLink>
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default TimeSheetExportModal;
